import React from "react";
import {
  AbsoluteFill,
  Audio,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import {
  Bot,
  Brain,
  Car,
  CheckCircle2,
  Clock,
  Compass,
  Cpu,
  FileCode,
  Globe,
  MapPin,
  Moon,
  MousePointer,
  Navigation,
  QrCode,
  Sparkles,
  Terminal,
  Workflow,
  XCircle,
  Zap,
} from "lucide-react";
import { DynamicSubtitle, SubtitleItem } from "./components/DynamicSubtitle";

// 21 Timed Subtitles synced precisely with BOZ voiceover
const SUBTITLES: SubtitleItem[] = [
  {
    from: 0,
    to: 104,
    text: "Banyak yang ngira semua AI itu sama kayak ChatGPT.",
    highlight: "ChatGPT",
  },
  {
    from: 104,
    to: 268,
    text: "Padahal ada satu lompatan besar yang belum banyak orang tau: AI Agent.",
    highlight: "AI Agent",
  },
  {
    from: 268,
    to: 377,
    text: "Ibaratnya gini: ChatGPT itu kayak Google Maps,",
    highlight: "Google Maps",
  },
  {
    from: 377,
    to: 476,
    text: "dia pinter banget ngasih rute, jarak, dan belokan.",
    highlight: "rute, jarak, dan belokan",
  },
  {
    from: 476,
    to: 599,
    text: "Tapi yang megang setir, injek rem, dan pegel nyetir lewatin macet...",
    highlight: "pegel nyetir",
  },
  {
    from: 599,
    to: 652,
    text: "...ya tetep tangan lo sendiri.",
    highlight: "tetep tangan lo sendiri",
  },
  {
    from: 652,
    to: 818,
    text: "Nah, kalau Hermes AI Agent, dia bukan peta—dia supir pribadi lo.",
    highlight: "supir pribadi",
  },
  {
    from: 818,
    to: 956,
    text: "Lo tinggal duduk santai di jok belakang, dia yang bawa mobilnya sampai tujuan.",
    highlight: "sampai tujuan",
  },
  {
    from: 956,
    to: 1004,
    text: "Kenapa bisa gitu?",
    highlight: "Kenapa bisa gitu?",
  },
  {
    from: 1004,
    to: 1152,
    text: "Karena Hermes bukan cuma punya otak buat mikir, tapi punya tangan dan kaki.",
    highlight: "tangan dan kaki",
  },
  {
    from: 1152,
    to: 1260,
    text: "Dia beneran bisa buka browser, klik tombol, ngetik form...",
    highlight: "buka browser, klik tombol",
  },
  {
    from: 1260,
    to: 1327,
    text: "...sampai nyelesaiin tugas di komputer.",
    highlight: "nyelesaiin tugas di komputer",
  },
  {
    from: 1327,
    to: 1466,
    text: "Contoh paling gila: pas lo mau war tiket konser atau kereta lebaran.",
    highlight: "war tiket konser",
  },
  {
    from: 1466,
    to: 1604,
    text: "Chatbot biasa paling cuma ngasih tips: \"Pastikan koneksi internet stabil ya\"...",
    highlight: "Chatbot biasa cuma ngasih tips",
  },
  {
    from: 1604,
    to: 1675,
    text: "...gak nolong pas server down!",
    highlight: "gak nolong pas server down",
  },
  {
    from: 1675,
    to: 1814,
    text: "Tapi Hermes lo suruh pantau webnya, dia stand-by 24 jam...",
    highlight: "stand-by 24 jam",
  },
  {
    from: 1814,
    to: 1916,
    text: "...pas slotnya kebuka, dia isi data lo dalam hitungan detik!",
    highlight: "hitungan detik",
  },
  {
    from: 1916,
    to: 2002,
    text: "Lo tinggal terima beres, scan, bayar.",
    highlight: "scan, bayar",
  },
  {
    from: 2002,
    to: 2135,
    text: "Masa depan AI bukan lagi soal siapa yang pinter ngetik prompt...",
    highlight: "pinter ngetik prompt",
  },
  {
    from: 2135,
    to: 2299,
    text: "...tapi siapa yang punya asisten otonom buat kerja pas kita tinggal tidur.",
    highlight: "asisten otonom",
  },
  {
    from: 2299,
    to: 2435,
    text: "Ini Hermes Agent: bukan cuma jawab... tapi bertindak.",
    highlight: "tapi bertindak",
  },
];

export const HermesAgentExplainer: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Neo-Brutalist & Swiss Palette
  const bgMain = "#0D110F"; // Dark Matte Pine
  const paperCream = "#F6F3EB"; // Editorial Paper
  const acidLime = "#E2F163"; // Neo-Streetwear Acid Punch
  const alertRed = "#FF4B4B";
  const softGray = "#9CA3AF";
  const cardBorder = "3px solid rgba(255, 255, 255, 0.16)";
  const brutalShadow = "8px 8px 0px #000000";

  // Calibrated Spring Helper
  const sp = (delay: number, damping = 14, stiffness = 160) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping, stiffness, mass: 1 },
    });

  // Global Progress [0 to 2435 frames]
  const globalProgress = interpolate(frame, [0, 2435], [0, 100], {
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: bgMain,
        color: paperCream,
        fontFamily: "'Rajdhani', sans-serif",
        overflow: "hidden",
      }}
    >
      {/* 1. Primary Voiceover Track (Mastered to -16 LUFS) */}
      <Audio
        src={staticFile("audio/hermes_agent_vo_mastered.mp3")}
        volume={1.0}
      />

      {/* 2. Ambient BGM with subtle bed volume */}
      <Audio
        src={staticFile("bgm-clean.mp3")}
        volume={0.06}
        loop
      />

      {/* 3. Dynamic SFX Cues */}
      {frame === 5 && <Audio src={staticFile("ui-pop.mp3")} volume={0.6} />}
      {frame === 105 && <Audio src={staticFile("swoosh-quick.mp3")} volume={0.5} />}
      {frame === 270 && <Audio src={staticFile("click.mp3")} volume={0.5} />}
      {frame === 480 && <Audio src={staticFile("sfx_error_buzzer.mp3")} volume={0.3} />}
      {frame === 655 && <Audio src={staticFile("chime.mp3")} volume={0.65} />}
      {frame === 960 && <Audio src={staticFile("whoosh.mp3")} volume={0.5} />}
      {frame === 1330 && <Audio src={staticFile("tick.mp3")} volume={0.5} />}
      {frame === 1470 && <Audio src={staticFile("sfx_error_buzzer.mp3")} volume={0.4} />}
      {frame === 1680 && <Audio src={staticFile("ui-success.mp3")} volume={0.7} />}
      {frame === 1920 && <Audio src={staticFile("sfx_cash_register.mp3")} volume={0.6} />}
      {frame === 2300 && <Audio src={staticFile("chime.mp3")} volume={0.8} />}

      {/* ========================================================================= */}
      {/* TOP HEADER — 100% PERFECTLY CENTERED (Y: 180px - 300px) */}
      {/* ========================================================================= */}
      <div
        style={{
          position: "absolute",
          top: 180,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          zIndex: 50,
        }}
      >
        <div
          style={{
            width: 940,
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {/* Brand Tag - Larger & Bolder */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                backgroundColor: acidLime,
                color: "#000000",
                padding: "10px 22px",
                borderRadius: 10,
                border: "2px solid #000000",
                boxShadow: "4px 4px 0px #000000",
                fontWeight: 800,
                fontSize: 22,
                fontFamily: "'Chakra Petch', sans-serif",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              <Bot size={26} strokeWidth={2.5} />
              HERMES AI AGENT
            </div>

            {/* Episode Tag */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                backgroundColor: "rgba(255, 255, 255, 0.08)",
                padding: "10px 20px",
                borderRadius: 10,
                border: "1px solid rgba(255, 255, 255, 0.16)",
                fontSize: 18,
                fontWeight: 700,
                color: "#E5E7EB",
                letterSpacing: "0.08em",
              }}
            >
              <Sparkles size={20} color={acidLime} />
              EDUKASI TEKNOLOGI
            </div>
          </div>

          {/* Global Progress Bar */}
          <div
            style={{
              width: "100%",
              height: 6,
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${globalProgress}%`,
                height: "100%",
                backgroundColor: acidLime,
              }}
            />
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* MAIN STAGE (Y: 300px - 1440px) — 100% PERFECTLY CENTERED */}
      {/* ========================================================================= */}
      <div
        style={{
          position: "absolute",
          top: 300,
          bottom: 440, // Above subtitle safe zone
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 20,
        }}
      >
        <div
          style={{
            width: 940,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* ----------------------------------------------------------------------- */}
          {/* SCENE 1: HOOK & THE MISCONCEPTION (Frames 0 - 268) */}
          {/* ----------------------------------------------------------------------- */}
          {frame >= 0 && frame < 268 && (
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 36,
                alignItems: "center",
              }}
            >
              {/* Massive Title Stamp */}
              <div
                style={{
                  fontFamily: "'Anton', sans-serif",
                  fontSize: 72,
                  lineHeight: 1.05,
                  textAlign: "center",
                  textTransform: "uppercase",
                  letterSpacing: "0.03em",
                  color: "#FFFFFF",
                  transform: `scale(${sp(0)})`,
                }}
              >
                Semua AI <span style={{ color: acidLime }}>Sama Saja?</span>
              </div>

              {/* Large Split Comparison Cards */}
              <div
                style={{
                  width: "100%",
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 24,
                }}
              >
                {/* Card 1: Chatbot Biasa */}
                <div
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    border: cardBorder,
                    borderRadius: 22,
                    padding: 32,
                    display: "flex",
                    flexDirection: "column",
                    gap: 20,
                    transform: `translateY(${interpolate(sp(10), [0, 1], [40, 0])}px)`,
                    opacity: sp(10),
                    boxShadow: brutalShadow,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Bot size={48} color={softGray} />
                    <span
                      style={{
                        fontSize: 16,
                        fontWeight: 800,
                        backgroundColor: "rgba(255, 255, 255, 0.12)",
                        padding: "6px 14px",
                        borderRadius: 8,
                        color: softGray,
                      }}
                    >
                      CHATBOT
                    </span>
                  </div>
                  <div>
                    <div style={{ fontSize: 36, fontWeight: 800, color: "#FFFFFF" }}>
                      ChatGPT
                    </div>
                    <div style={{ fontSize: 20, color: softGray, marginTop: 6, lineHeight: 1.35 }}>
                      Cuma bisa ngetik teks jawaban & ngasih tutorial
                    </div>
                  </div>
                  <div
                    style={{
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      padding: 16,
                      borderRadius: 12,
                      fontSize: 17,
                      color: "#D1D5DB",
                      fontFamily: "monospace",
                      borderLeft: "4px solid #6B7280",
                    }}
                  >
                    "Ini 10 langkah cara menyelesaikannya..."
                  </div>
                </div>

                {/* Card 2: AI Agent */}
                <div
                  style={{
                    backgroundColor: "#161D19",
                    border: `3px solid ${acidLime}`,
                    boxShadow: brutalShadow,
                    borderRadius: 22,
                    padding: 32,
                    display: "flex",
                    flexDirection: "column",
                    gap: 20,
                    transform: `scale(${sp(110)})`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Zap size={48} color={acidLime} />
                    <span
                      style={{
                        fontSize: 16,
                        fontWeight: 800,
                        backgroundColor: acidLime,
                        color: "#000000",
                        padding: "6px 14px",
                        borderRadius: 8,
                      }}
                    >
                      AUTONOMOUS
                    </span>
                  </div>
                  <div>
                    <div style={{ fontSize: 36, fontWeight: 800, color: "#FFFFFF" }}>
                      AI Agent
                    </div>
                    <div style={{ fontSize: 20, color: acidLime, marginTop: 6, lineHeight: 1.35 }}>
                      Lompatan besar: Diberi tangan buat eksekusi
                    </div>
                  </div>
                  <div
                    style={{
                      backgroundColor: "rgba(226, 241, 99, 0.12)",
                      padding: 16,
                      borderRadius: 12,
                      fontSize: 17,
                      color: "#FFFFFF",
                      fontFamily: "monospace",
                      borderLeft: `4px solid ${acidLime}`,
                    }}
                  >
                    [SYSTEM] Tugas diselesaikan otomatis ✓
                  </div>
                </div>
              </div>

              {/* Sub-tag */}
              {frame > 120 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    backgroundColor: "rgba(255, 255, 255, 0.08)",
                    padding: "14px 32px",
                    borderRadius: 40,
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    fontSize: 22,
                    fontWeight: 700,
                    color: "#FFFFFF",
                    transform: `scale(${sp(120)})`,
                  }}
                >
                  <Compass size={26} color={acidLime} />
                  Mari bedah analogi paling gampangnya
                </div>
              )}
            </div>
          )}

          {/* ----------------------------------------------------------------------- */}
          {/* SCENE 2: ANALOGI 1 — GOOGLE MAPS & THE PAIN (Frames 268 - 652) */}
          {/* ----------------------------------------------------------------------- */}
          {frame >= 268 && frame < 652 && (
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 32,
                alignItems: "center",
              }}
            >
              {/* Massive Header Badge */}
              <div
                style={{
                  fontFamily: "'Anton', sans-serif",
                  fontSize: 68,
                  color: "#FFFFFF",
                  textAlign: "center",
                  textTransform: "uppercase",
                  letterSpacing: "0.03em",
                  transform: `scale(${sp(270)})`,
                }}
              >
                ChatGPT = <span style={{ color: "#60A5FA" }}>Google Maps</span>
              </div>

              {/* Google Maps Simulation Card - Much Bigger & Bolder */}
              <div
                style={{
                  width: "100%",
                  backgroundColor: "#161D19",
                  border: frame > 476 ? `3px solid ${alertRed}` : cardBorder,
                  borderRadius: 24,
                  padding: 36,
                  boxShadow: brutalShadow,
                  display: "flex",
                  flexDirection: "column",
                  gap: 26,
                  transition: "border 0.3s ease",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                    <Navigation size={48} color="#60A5FA" />
                    <div>
                      <div style={{ fontSize: 30, fontWeight: 800, color: "#FFFFFF" }}>
                        Navigasi Rute Aktif
                      </div>
                      <div style={{ fontSize: 20, color: softGray, marginTop: 4 }}>
                        15 km • 28 Menit • Macet Padat
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      backgroundColor: "rgba(96, 165, 250, 0.18)",
                      color: "#93C5FD",
                      padding: "10px 20px",
                      borderRadius: 10,
                      fontWeight: 800,
                      fontSize: 18,
                    }}
                  >
                    PINTAR BANGET
                  </div>
                </div>

                {/* Step Directions */}
                <div
                  style={{
                    backgroundColor: "rgba(0, 0, 0, 0.45)",
                    padding: 22,
                    borderRadius: 16,
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 14, fontSize: 22 }}>
                    <MapPin size={26} color="#60A5FA" />
                    <span>200m lagi: Belok kanan lewatin lampu merah</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 14, fontSize: 22 }}>
                    <MapPin size={26} color="#60A5FA" />
                    <span>500m lagi: Ambil jalur kiri hindari perbaikan jalan</span>
                  </div>
                </div>

                {/* The Pain Point Reveal (Frame > 476) - Huge Alert */}
                {frame >= 476 && (
                  <div
                    style={{
                      backgroundColor: "rgba(255, 75, 75, 0.16)",
                      border: `3px solid ${alertRed}`,
                      padding: 26,
                      borderRadius: 16,
                      display: "flex",
                      alignItems: "center",
                      gap: 20,
                      transform: `scale(${sp(480)})`,
                    }}
                  >
                    <XCircle size={52} color={alertRed} />
                    <div>
                      <div style={{ fontSize: 26, fontWeight: 800, color: alertRed }}>
                        TAPI LO TETEP PEGEL NYETIR!
                      </div>
                      <div style={{ fontSize: 19, color: "#FCA5A5", marginTop: 4 }}>
                        Lo yang megang setir, injek rem, oper gigi, dan stres lewatin macet.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ----------------------------------------------------------------------- */}
          {/* SCENE 3: ANALOGI 2 — SUPIR PRIBADI (Frames 652 - 956) */}
          {/* ----------------------------------------------------------------------- */}
          {frame >= 652 && frame < 956 && (
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 32,
                alignItems: "center",
              }}
            >
              {/* Massive Header Badge */}
              <div
                style={{
                  fontFamily: "'Anton', sans-serif",
                  fontSize: 68,
                  color: "#FFFFFF",
                  textAlign: "center",
                  textTransform: "uppercase",
                  letterSpacing: "0.03em",
                  transform: `scale(${sp(655)})`,
                }}
              >
                Hermes = <span style={{ color: acidLime }}>Supir Pribadi</span>
              </div>

              {/* Chauffeur Experience Card - Big & Bold */}
              <div
                style={{
                  width: "100%",
                  backgroundColor: "#161D19",
                  border: `3px solid ${acidLime}`,
                  borderRadius: 24,
                  padding: 36,
                  boxShadow: brutalShadow,
                  display: "flex",
                  flexDirection: "column",
                  gap: 28,
                  transform: `translateY(${interpolate(sp(660), [0, 1], [30, 0])}px)`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                    <Car size={56} color={acidLime} />
                    <div>
                      <div style={{ fontSize: 32, fontWeight: 800, color: "#FFFFFF" }}>
                        CHAUFFEUR SERVICE
                      </div>
                      <div style={{ fontSize: 20, color: acidLime, marginTop: 4 }}>
                        Status: Lo Duduk Santai di Belakang
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      backgroundColor: acidLime,
                      color: "#000000",
                      padding: "8px 20px",
                      borderRadius: 10,
                      fontWeight: 800,
                      fontSize: 18,
                    }}
                  >
                    OTONOM
                  </div>
                </div>

                {/* Benefit Checklist - Large Font */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                  }}
                >
                  {[
                    { text: "Gak perlu mikirin rute & capek nyetir", delay: 680 },
                    { text: "Lo bebas santai, ngopi, atau tidur", delay: 720 },
                    { text: "Tahu-tahu mobil udah sampai di lobi tujuan", delay: 760 },
                  ].map((item, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 18,
                        backgroundColor: "rgba(0, 0, 0, 0.4)",
                        padding: "18px 24px",
                        borderRadius: 14,
                        fontSize: 22,
                        fontWeight: 700,
                        transform: `translateX(${interpolate(sp(item.delay), [0, 1], [-20, 0])}px)`,
                        opacity: sp(item.delay),
                      }}
                    >
                      <CheckCircle2 size={30} color={acidLime} />
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    textAlign: "center",
                    fontSize: 20,
                    color: softGray,
                    fontStyle: "italic",
                  }}
                >
                  "Lo cuma tentukan tujuan akhir, Hermes yang jalanin kendaraannya."
                </div>
              </div>
            </div>
          )}

          {/* ----------------------------------------------------------------------- */}
          {/* SCENE 4: ANATOMI — OTAK VS TANGAN & KAKI (Frames 956 - 1327) */}
          {/* ----------------------------------------------------------------------- */}
          {frame >= 956 && frame < 1327 && (
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 28,
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontFamily: "'Anton', sans-serif",
                  fontSize: 64,
                  color: "#FFFFFF",
                  textAlign: "center",
                  textTransform: "uppercase",
                  letterSpacing: "0.03em",
                  transform: `scale(${sp(960)})`,
                }}
              >
                Punya <span style={{ color: acidLime }}>Otak</span> +{" "}
                <span style={{ color: "#60A5FA" }}>Tangan & Kaki</span>
              </div>

              {/* Bento Grid Tools - Larger */}
              <div
                style={{
                  width: "100%",
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 20,
                }}
              >
                {/* Brain Card */}
                <div
                  style={{
                    backgroundColor: "#161D19",
                    border: cardBorder,
                    borderRadius: 20,
                    padding: 28,
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                    boxShadow: brutalShadow,
                    transform: `scale(${sp(980)})`,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <Brain size={40} color={acidLime} />
                    <span style={{ fontSize: 26, fontWeight: 800, color: "#FFFFFF" }}>
                      OTAK (LLM)
                    </span>
                  </div>
                  <div style={{ fontSize: 18, color: softGray, lineHeight: 1.45 }}>
                    Menganalisa, menyusun strategi, & memecahkan masalah.
                  </div>
                </div>

                {/* Action Hand Card */}
                <div
                  style={{
                    backgroundColor: "#161D19",
                    border: `3px solid ${acidLime}`,
                    borderRadius: 20,
                    padding: 28,
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                    boxShadow: brutalShadow,
                    transform: `scale(${sp(1000)})`,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <Workflow size={40} color="#60A5FA" />
                    <span style={{ fontSize: 26, fontWeight: 800, color: "#FFFFFF" }}>
                      TANGAN (TOOLS)
                    </span>
                  </div>
                  <div style={{ fontSize: 18, color: softGray, lineHeight: 1.45 }}>
                    Eksekutor nyata yang mengendalikan tools di komputer.
                  </div>
                </div>
              </div>

              {/* 4 Kinetic Execution Abilities - Large Cards */}
              <div
                style={{
                  width: "100%",
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 18,
                }}
              >
                {[
                  { icon: Globe, label: "Buka & Kontrol Browser", color: "#60A5FA", delay: 1080 },
                  { icon: MousePointer, label: "Klik Tombol & Navigasi", color: "#34D399", delay: 1120 },
                  { icon: FileCode, label: "Ngetik Form & Input Data", color: "#FBBF24", delay: 1160 },
                  { icon: Terminal, label: "Jalanin Script & Komando", color: acidLime, delay: 1200 },
                ].map((item, i) => {
                  const IconComponent = item.icon;
                  return (
                    <div
                      key={i}
                      style={{
                        backgroundColor: "rgba(255, 255, 255, 0.06)",
                        border: "2px solid rgba(255, 255, 255, 0.14)",
                        padding: "22px 20px",
                        borderRadius: 16,
                        display: "flex",
                        alignItems: "center",
                        gap: 16,
                        boxShadow: "4px 4px 0px #000000",
                        transform: `translateY(${interpolate(sp(item.delay), [0, 1], [20, 0])}px)`,
                        opacity: sp(item.delay),
                      }}
                    >
                      <IconComponent size={32} color={item.color} />
                      <span style={{ fontSize: 20, fontWeight: 700, color: "#FFFFFF" }}>
                        {item.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ----------------------------------------------------------------------- */}
          {/* SCENE 5: KASUS NYATA — WAR TIKET & CHATBOT BIASA (Frames 1327 - 1675) */}
          {/* ----------------------------------------------------------------------- */}
          {frame >= 1327 && frame < 1675 && (
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 28,
                alignItems: "center",
              }}
            >
              {/* Header */}
              <div
                style={{
                  fontFamily: "'Anton', sans-serif",
                  fontSize: 64,
                  color: "#FFFFFF",
                  textAlign: "center",
                  textTransform: "uppercase",
                  letterSpacing: "0.03em",
                  transform: `scale(${sp(1330)})`,
                }}
              >
                Kasus: <span style={{ color: "#F87171" }}>War Tiket Konser</span>
              </div>

              {/* Countdown Clock Display - Big */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  backgroundColor: "rgba(0, 0, 0, 0.65)",
                  padding: "14px 32px",
                  borderRadius: 40,
                  border: "2px solid rgba(255, 255, 255, 0.2)",
                  fontSize: 26,
                  fontWeight: 800,
                  fontFamily: "monospace",
                  color: "#F87171",
                  boxShadow: brutalShadow,
                }}
              >
                <Clock size={28} />
                COUNTDOWN: 11:59:59 → 12:00:00
              </div>

              {/* Chatbot Advice Card */}
              <div
                style={{
                  width: "100%",
                  backgroundColor: "#161D19",
                  border: cardBorder,
                  borderRadius: 24,
                  padding: 32,
                  boxShadow: brutalShadow,
                  display: "flex",
                  flexDirection: "column",
                  gap: 22,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <Bot size={36} color={softGray} />
                  <span style={{ fontSize: 24, fontWeight: 800, color: softGray }}>
                    Respon Chatbot Biasa:
                  </span>
                </div>

                <div
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.06)",
                    padding: 22,
                    borderRadius: 14,
                    fontSize: 22,
                    color: "#E5E7EB",
                    lineHeight: 1.45,
                    borderLeft: "5px solid #9CA3AF",
                  }}
                >
                  "Tips war tiket: Pastikan koneksi internet stabil ya, buka web 10 menit lebih awal, dan jangan panik!"
                </div>

                {/* Red Stamp Overlay - Huge */}
                {frame >= 1470 && (
                  <div
                    style={{
                      backgroundColor: "rgba(255, 75, 75, 0.18)",
                      border: `3px solid ${alertRed}`,
                      padding: 24,
                      borderRadius: 16,
                      display: "flex",
                      alignItems: "center",
                      gap: 20,
                      transform: `scale(${sp(1475)})`,
                    }}
                  >
                    <XCircle size={46} color={alertRed} />
                    <div>
                      <div style={{ fontSize: 26, fontWeight: 800, color: alertRed }}>
                        GAK NOLONG PAS SERVER DOWN!
                      </div>
                      <div style={{ fontSize: 18, color: "#FECACA", marginTop: 4 }}>
                        Cuma modal saran teks, tapi tiket udah ludes dalam 2 detik.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ----------------------------------------------------------------------- */}
          {/* SCENE 6: HERMES ACTION — 24 JAM & INSTANT FILL (Frames 1675 - 2002) */}
          {/* ----------------------------------------------------------------------- */}
          {frame >= 1675 && frame < 2002 && (
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 28,
                alignItems: "center",
              }}
            >
              {/* Header */}
              <div
                style={{
                  fontFamily: "'Anton', sans-serif",
                  fontSize: 64,
                  color: "#FFFFFF",
                  textAlign: "center",
                  textTransform: "uppercase",
                  letterSpacing: "0.03em",
                  transform: `scale(${sp(1680)})`,
                }}
              >
                Hermes <span style={{ color: acidLime }}>Live Action</span>
              </div>

              {/* Live Automated Watcher Card - Big */}
              <div
                style={{
                  width: "100%",
                  backgroundColor: "#161D19",
                  border: `3px solid ${acidLime}`,
                  borderRadius: 24,
                  padding: 32,
                  boxShadow: brutalShadow,
                  display: "flex",
                  flexDirection: "column",
                  gap: 20,
                }}
              >
                {/* Terminal Header */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: "2px solid rgba(255, 255, 255, 0.12)",
                    paddingBottom: 14,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <Terminal size={30} color={acidLime} />
                    <span style={{ fontSize: 20, fontWeight: 800, color: "#FFFFFF" }}>
                      HERMES AUTONOMOUS RUNNER
                    </span>
                  </div>
                  <div
                    style={{
                      backgroundColor: "rgba(226, 241, 99, 0.18)",
                      color: acidLime,
                      padding: "6px 14px",
                      borderRadius: 8,
                      fontSize: 16,
                      fontWeight: 800,
                    }}
                  >
                    24 JAM STAND-BY
                  </div>
                </div>

                {/* Execution Steps */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                    fontFamily: "monospace",
                    fontSize: 18,
                  }}
                >
                  <div style={{ color: "#9CA3AF" }}>
                    [00:00.01] Monitoring portal tiket (Ping: 22ms)...
                  </div>
                  <div style={{ color: acidLime, fontWeight: 700 }}>
                    [12:00.00] ⚡ SLOT TERBUKA! TRIGGER AUTONOMOUS FILL
                  </div>
                  <div style={{ color: "#E5E7EB" }}>
                    [12:00.01] Mengisi Nama: Zura | NIK: Terverifikasi
                  </div>
                  <div style={{ color: "#E5E7EB" }}>
                    [12:00.02] Kunci Tiket VIP Seat A-12 (Sukses)
                  </div>
                </div>

                {/* QRIS / Checkout Ready Stamp */}
                {frame >= 1916 && (
                  <div
                    style={{
                      backgroundColor: "rgba(0, 0, 0, 0.6)",
                      border: `2px solid ${acidLime}`,
                      borderRadius: 16,
                      padding: 22,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      transform: `scale(${sp(1920)})`,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                      <QrCode size={52} color={acidLime} />
                      <div>
                        <div style={{ fontSize: 24, fontWeight: 800, color: "#FFFFFF" }}>
                          TIKET BERHASIL DIAMANKAN!
                        </div>
                        <div style={{ fontSize: 18, color: softGray, marginTop: 2 }}>
                          Tinggal terima beres, scan, bayar.
                        </div>
                      </div>
                    </div>
                    <CheckCircle2 size={36} color={acidLime} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ----------------------------------------------------------------------- */}
          {/* SCENE 7: PARADIGM SHIFT — DITINGGAL TIDUR (Frames 2002 - 2299) */}
          {/* ----------------------------------------------------------------------- */}
          {frame >= 2002 && frame < 2299 && (
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 32,
                alignItems: "center",
              }}
            >
              {/* Header */}
              <div
                style={{
                  fontFamily: "'Anton', sans-serif",
                  fontSize: 64,
                  color: "#FFFFFF",
                  textAlign: "center",
                  textTransform: "uppercase",
                  letterSpacing: "0.03em",
                  transform: `scale(${sp(2005)})`,
                }}
              >
                Masa Depan <span style={{ color: acidLime }}>Bukan Ngetik Prompt</span>
              </div>

              {/* Shift Card - Big & Bold */}
              <div
                style={{
                  width: "100%",
                  backgroundColor: "#161D19",
                  border: cardBorder,
                  borderRadius: 24,
                  padding: 36,
                  boxShadow: brutalShadow,
                  display: "flex",
                  flexDirection: "column",
                  gap: 26,
                }}
              >
                {/* Old vs New */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    backgroundColor: "rgba(255, 255, 255, 0.06)",
                    padding: "18px 26px",
                    borderRadius: 14,
                  }}
                >
                  <span style={{ fontSize: 20, color: softGray }}>Dulu: Jago Ngetik Prompt</span>
                  <span style={{ fontSize: 22, fontWeight: 800, color: acidLime }}>
                    Sekarang: Punya Asisten Mandiri
                  </span>
                </div>

                {/* Big Sleep Benefit */}
                <div
                  style={{
                    backgroundColor: "rgba(226, 241, 99, 0.1)",
                    border: `3px solid ${acidLime}`,
                    borderRadius: 20,
                    padding: 32,
                    display: "flex",
                    alignItems: "center",
                    gap: 26,
                    transform: `scale(${sp(2135)})`,
                  }}
                >
                  <Moon size={64} color={acidLime} />
                  <div>
                    <div style={{ fontSize: 32, fontWeight: 800, color: "#FFFFFF" }}>
                      KERJA PAS DITINGGAL TIDUR
                    </div>
                    <div style={{ fontSize: 20, color: "#D1D5DB", marginTop: 6, lineHeight: 1.4 }}>
                      Bangun pagi, semua tugas rumit lo udah beres di komputer.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ----------------------------------------------------------------------- */}
          {/* SCENE 8: OUTRO & PUNCHLINE (Frames 2299 - 2435) */}
          {/* ----------------------------------------------------------------------- */}
          {frame >= 2299 && (
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 32,
                alignItems: "center",
                textAlign: "center",
              }}
            >
              {/* Big Logo Stamp */}
              <div
                style={{
                  width: 120,
                  height: 120,
                  borderRadius: 30,
                  backgroundColor: acidLime,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "4px solid #000000",
                  boxShadow: brutalShadow,
                  transform: `scale(${sp(2300)})`,
                }}
              >
                <Cpu size={72} color="#000000" strokeWidth={2.5} />
              </div>

              <div
                style={{
                  fontFamily: "'Anton', sans-serif",
                  fontSize: 84,
                  color: "#FFFFFF",
                  letterSpacing: "0.04em",
                  lineHeight: 1,
                  transform: `scale(${sp(2305)})`,
                }}
              >
                HERMES AGENT
              </div>

              <div
                style={{
                  backgroundColor: "#161D19",
                  border: `3px solid ${acidLime}`,
                  padding: "22px 46px",
                  borderRadius: 18,
                  boxShadow: brutalShadow,
                  fontSize: 32,
                  fontWeight: 800,
                  color: acidLime,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  transform: `translateY(${interpolate(sp(2320), [0, 1], [30, 0])}px)`,
                }}
              >
                Bukan Cuma Jawab... Tapi Bertindak.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* DYNAMIC SUBTITLES — STRICT TIKTOK SAFE ZONE (Bottom: 380px / Y: 1540px) */}
      {/* ========================================================================= */}
      <DynamicSubtitle
        frame={frame}
        subtitles={SUBTITLES}
        bottom={380} // Strictly safe above bottom nav/caption
        highlightColor={acidLime}
        backgroundColor="rgba(13, 17, 15, 0.95)"
        fontSize={32}
      />
    </AbsoluteFill>
  );
};
